<template>
  <div class="auth-shell">
    <main class="auth-panel">
      <div class="auth-panel__inner">
        <div class="auth-top">
          <a class="auth-logo-link" :href="$store.state.config.WEBSITE_URL">
            <img
              :src="$store.state.config.LOGO_URL"
              class="auth-logo"
              alt="1LINK.IO"
            />
          </a>
          <router-link class="auth-switch" to="/login" id="login-page-btn">
            Already have an account? <strong>Login</strong>
          </router-link>
        </div>

        <div class="auth-body">
          <p class="ln-eyebrow">Get started</p>
          <h1 class="ln-display">Create your account</h1>
          <p class="ln-lede">
            Start shortening, branding and tracking your links in a couple of
            minutes.
          </p>

          <button
            type="button"
            class="ln-btn ln-btn--quiet auth-google"
            @click="signupViaGoogle"
          >
            <v-icon size="small" class="ln-btn__icon">mdi-google</v-icon>
            Continue with Google
          </button>

          <div class="auth-divider">
            <span>or with email</span>
          </div>

          <p
            v-if="invitation"
            class="auth-invite-note"
            data-testid="invite-note"
          >
            Use {{ invitation.email }} to join {{ invitation.teamName }}.
          </p>

          <form @submit.prevent="signupAccount">
            <div class="ln-field">
              <v-text-field
                v-model="email"
                id="email"
                label="Email"
                name="login"
                type="text"
                autocomplete="email"
                variant="outlined"
                density="compact"
                hide-details="auto"
              />
            </div>

            <div class="ln-field">
              <v-text-field
                id="password"
                v-model="password"
                :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPassword ? 'text' : 'password'"
                label="Password"
                name="password"
                autocomplete="new-password"
                variant="outlined"
                density="compact"
                hide-details="auto"
                @click:append-inner="showPassword = !showPassword"
              />
            </div>

            <div class="ln-field">
              <v-text-field
                id="confirmPassword"
                v-model="confirmPassword"
                :append-inner-icon="
                  showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'
                "
                :type="showConfirmPassword ? 'text' : 'password'"
                label="Confirm password"
                name="confirmPassword"
                autocomplete="new-password"
                variant="outlined"
                density="compact"
                hide-details="auto"
                @click:append-inner="showConfirmPassword = !showConfirmPassword"
              />
            </div>

            <div class="auth-checks">
              <v-checkbox
                v-model="agree"
                id="terms-checkbox"
                color="primary"
                density="compact"
                hide-details
              >
                <template v-slot:label>
                  <div class="auth-check__label">
                    I agree to 1LINK.IO's
                    <a href="/#/terms-of-service" target="_blank" @click.stop
                      >Terms of Service</a
                    >
                    and
                    <a href="/#/privacy-policy" target="_blank" @click.stop
                      >Privacy Policy</a
                    >
                  </div>
                </template>
              </v-checkbox>
            </div>

            <button
              type="submit"
              class="ln-btn ln-btn--solid auth-submit"
              id="signup-btn"
              :disabled="!agree"
            >
              Signup
              <v-icon size="small" class="ln-btn__icon"
                >mdi-chevron-right</v-icon
              >
            </button>
          </form>
        </div>
      </div>
    </main>

    <auth-showcase />
  </div>
</template>

<script>
import { signup, signupViaGoogle, sendEmailVerification, auth } from "../auth";
import { initAccount } from "../api";
import { capture } from "../analytics";
import { getPendingInvitation } from "../pendingInvitation";
import AuthShowcase from "../components/AuthShowcase.vue";

export default {
  components: { AuthShowcase },
  data() {
    const invitation = getPendingInvitation();
    return {
      agree: false,
      email: invitation ? invitation.email : null,
      invitation,
      password: null,
      confirmPassword: null,
      showPassword: false,
      showConfirmPassword: false,
    };
  },
  mounted() {
    this.$store.commit("setIsLoading", false);
  },
  methods: {
    initAccount() {
      try {
        if (window.tolt_referral) {
          const initialUser = auth().currentUser;
          if (initialUser && initialUser.email) {
            window.tolt.signup(initialUser.email);
          }
        }
      } catch (err) {
        console.error(err);
      }

      return initAccount();
    },
    async signupViaGoogle() {
      this.$store.commit("setIsLoading", true);
      capture("signup_submitted", { method: "google" });
      try {
        await signupViaGoogle();
        // await sendEmailVerification();
        await this.initAccount();

        const initialUser = auth().currentUser;
        this.$store.commit("setUser", initialUser);

        this.$router.push("/almost-done");
      } catch (err) {
        console.error(err);
        capture("signup_failed", {
          method: "google",
          code: err.code,
          message: err.message,
        });
        if (err.code !== "auth/popup-closed-by-user") {
          window.alert(err.message);
        }
      } finally {
        this.$store.commit("setIsLoading", false);
      }
    },
    async signupAccount() {
      // trim/lowercase: mobile autofill routinely appends whitespace, which
      // Firebase rejects with "The email address is badly formatted." instead
      // of a message that tells the user what's wrong
      const email =
        typeof this.email === "string"
          ? this.email.trim().toLowerCase()
          : this.email;
      if (!email || !this.password || this.password !== this.confirmPassword) {
        capture("signup_failed", {
          method: "password",
          code:
            email && this.password
              ? "client/password_mismatch"
              : "client/missing_fields",
        });
        window.alert("Email or password not valid");
        return;
      }
      this.$store.commit("setIsLoading", true);
      capture("signup_submitted", { method: "password" });
      try {
        await signup(email, this.password);
        // await sendEmailVerification();
        await this.initAccount();

        const initialUser = auth().currentUser;
        this.$store.commit("setUser", initialUser);

        this.$router.push("/verify-email");
      } catch (err) {
        console.error(err);
        window.alert(err.message);
        capture("signup_failed", {
          method: "password",
          code: err.code,
          message: err.message,
        });
      } finally {
        this.$store.commit("setIsLoading", false);
      }
    },
  },
};
</script>

<style scoped>
/* ---------- shell ------------------------------------------------------- */

.auth-shell {
  min-height: 100dvh;
  display: grid;
  grid-template-columns: 1fr;
  background: var(--ln-canvas);
}

.auth-panel {
  display: flex;
  min-height: 100dvh;
  padding: 24px 20px 40px;
}

.auth-panel__inner {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

/* ---------- top row ------------------------------------------------------ */

.auth-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 40px;
}

.auth-logo-link {
  display: inline-flex;
}

.auth-logo {
  height: 24px;
  width: auto;
}

.auth-switch {
  font-family: sans-serif;
  font-size: 13px;
  color: var(--ln-muted);
  text-decoration: none;
  white-space: nowrap;
}

.auth-switch strong {
  font-weight: 500;
  color: var(--ln-ink);
}

.auth-switch:hover strong {
  text-decoration: underline;
}

/* ---------- body ---------------------------------------------------------- */

.auth-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-bottom: 40px;
}

.ln-eyebrow {
  margin: 0 0 10px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-display {
  margin: 0;
  font-family: sans-serif;
  font-size: 32px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ln-ink);
}

.ln-lede {
  margin: 10px 0 28px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-muted);
}

/* ---------- buttons -------------------------------------------------------- */

.ln-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 16px;
  height: 38px;
  border: 1px solid transparent;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease),
    transform 120ms var(--ln-ease);
}

.ln-btn:active {
  transform: scale(0.98);
}

.ln-btn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 2px;
}

.ln-btn__icon.v-icon {
  font-size: 15px;
  color: inherit;
}

.ln-btn--solid {
  background: var(--ln-accent);
  color: #ffffff;
}

.ln-btn--solid:hover {
  background: var(--ln-accent-hover);
}

.ln-btn--quiet {
  border-color: var(--ln-line);
  background: var(--ln-surface);
  color: var(--ln-ink-2);
}

.ln-btn--quiet:hover {
  background: var(--ln-surface-2);
  border-color: #dcdcdc;
}

.ln-btn:disabled {
  opacity: 0.45;
  cursor: default;
  transform: none;
}

.auth-google {
  width: 100%;
}

.auth-submit {
  width: 100%;
  height: 44px;
  font-size: 14px;
  margin-top: 10px;
}

/* ---------- divider --------------------------------------------------------- */

.auth-divider {
  position: relative;
  margin: 22px 0;
  text-align: center;
}

.auth-divider::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--ln-line);
}

.auth-divider span {
  position: relative;
  padding: 0 12px;
  background: var(--ln-surface);
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

/* ---------- fields ----------------------------------------------------------- */

.ln-field {
  margin-bottom: 16px;
}

.auth-body :deep(.v-field__outline) {
  color: var(--ln-line);
  --v-field-border-opacity: 1;
}

.auth-body :deep(.v-field--focused .v-field__outline) {
  color: var(--ln-accent);
  --v-field-border-opacity: 1;
}

.auth-body :deep(.v-label) {
  font-family: sans-serif;
  font-size: 13px;
  color: var(--ln-muted);
}

.auth-body :deep(.v-field__input) {
  font-family: sans-serif;
  font-size: 14px;
  color: var(--ln-ink);
}

.auth-body :deep(.v-field .v-icon) {
  font-size: 18px;
  color: var(--ln-muted);
}

/* ---------- checkboxes -------------------------------------------------------- */

.auth-checks {
  margin: 4px 0 20px;
}

.auth-checks :deep(.v-checkbox) {
  margin-top: 6px;
}

.auth-checks :deep(.v-label) {
  font-family: sans-serif;
}

.auth-check__label {
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--ln-ink-2);
}

.auth-check__label a {
  color: var(--ln-ink);
  font-weight: 500;
}

.auth-invite-note {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.5;
}

/* ---------- breakpoints ------------------------------------------------------- */

@media (min-width: 960px) {
  .auth-shell {
    grid-template-columns: minmax(420px, 46%) 1fr;
  }

  .auth-panel {
    padding: 40px;
  }

  .ln-display {
    font-size: 38px;
  }
}
</style>
